/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Baranchikov
 * @version 1.27.0
 * @since 1.13.0
 */

import { createElement } from 'react';

import { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { makeSelectBreadCrumbsData } from '@/main/modules/breadCrumbs/breadCrumbsSelectors';
import { breadCrumbGoBack } from '@/main/modules/breadCrumbs/utils/breadCrumbsGoBack';
import { ModalMultipleOrders } from '@/main/modules/modal/components/ModalMultipleOrders';
import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';
import { HttpStatus } from '@modules/app/appConstants';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardExpensesDetail,
    makeSelectWizardTranslation,
} from '@modules/internationalization/intSelectors';
import { IWizardExpensesDetailTranslation } from '@modules/internationalization/types/wizards/expensesDetail';
import { modalOpen } from '@modules/modal/modalActions';
import { IModalRenderProps } from '@modules/modal/types/modalTypes';
import { EWizardReportParam } from '@modules/wizard/components/WizardReport/WizardReportConstants';
import {
    wizardReportStorageRestoreSaga,
    wizardReportValidateSaga,
} from '@modules/wizard/components/WizardReport/wizardReportSagas';
import {
    makeSelectWizardReportOrderParams,
    makeSelectWizardReportParam,
} from '@modules/wizard/components/WizardReport/wizardReportSelectors';
import {
    IWizardReportOrderParams,
    IWizardReportParam,
} from '@modules/wizard/components/WizardReport/wizardReportTypes';
import {
    wizardNextAction,
    wizardOpenModalSummaryAction,
    wizardStorageSaveStepAction,
    wizardStorageUpdateStepAction,
} from '@modules/wizard/wizardActions';
import {
    WIZARD_BACK_FAILED,
    WIZARD_BACK_SUCCESS,
    WIZARD_META_SUCCESS,
    WIZARD_NEXT_FAILED,
    WIZARD_NEXT_SUCCESS,
    WIZARD_RESTORE_FAILED,
    WIZARD_START_FAILED,
    WIZARD_START_SUCCESS,
} from '@modules/wizard/wizardConstants';
import { wizardModalTimeoutSaga, wizardStorageLoadStepsSaga } from '@modules/wizard/wizardSagas';
import { makeSelectWizardBasketSnapshotId, makeSelectWizardStepName } from '@modules/wizard/wizardSelectors';
import { TWizardMetaSuccessAction, TWizardNextSuccessAction } from '@modules/wizard/wizardTypes';

import { ICommonTranslation } from '../../modules/internationalization/types';
import ModalErrorCommon from '../../modules/modal/components/ModalError';
import { makeSelectIsFeatureWithVersionEnabled } from '../PageAuth/selectors/authSelectors';
import { TWizardExpensesDetailUpdateDataPayload } from './types/wizardExpensesDetailActionTypes';
import { IWizardExpensesDetailHierarchyLevel } from './types/wizardExpensesDetailTypes';
import {
    wizardExpensesDetailSetParamsAction,
    wizardExpensesDetailUpdateDataAction,
    wizardExpensesDetailValidationAction,
} from './wizardExpensesDetailActions';
import {
    EWizardExpensesDetailSteps,
    initWizardExpensesDetailState,
    WIZARD_EXPENSES_DETAIL_CHECK_VALIDATION,
    WIZARD_EXPENSES_DETAIL_HANDLE_NEXT,
    WIZARD_EXPENSES_DETAIL_UPDATE_DATA,
} from './wizardExpensesDetailConstant';
import { IWizardExpensesDetailReducer } from './wizardExpensesDetailReducer';
import {
    makeSelectWizardExpensesDetailDate,
    makeSelectWizardExpensesDetailFormatsAvailableChosen,
    makeSelectWizardExpensesDetailHierarchyLevelsChosen,
} from './wizardExpensesDetailSelectors';

/*
 * -------------------------------------------
 *           INITIALIZING
 * -------------------------------------------
 */

export function* wizardExpensesDetailMetaSaga(action: TWizardMetaSuccessAction): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());
    const storage: TWizardExpensesDetailUpdateDataPayload = yield call(wizardExpensesDetailLoadDataFromStorageSaga);

    switch (stepName) {
        case EWizardExpensesDetailSteps.StepSelectParams:
            const formatsAvailable: IWizardExpensesDetailReducer['formatsAvailable'] =
                action.payload.step.formatsAvailable;
            const hierarchyLevels: IWizardExpensesDetailReducer['hierarchyLevels'] =
                action.payload.step.hierarchyLevels;
            const maxDate = action.payload.step.maxDate;
            yield put(
                wizardExpensesDetailSetParamsAction({
                    formatsAvailable: formatsAvailable || [],
                    hierarchyLevels: (hierarchyLevels || []).sort((a, b) => a.order - b.order),
                    maxDate,
                }),
            );
            if (!(storage.formatsAvailableChosen && storage.hierarchyLevelsChosen)) {
                yield put(
                    wizardExpensesDetailUpdateDataAction({
                        date: maxDate,
                        formatsAvailableChosen:
                            formatsAvailable[0] || initWizardExpensesDetailState.formatsAvailableChosen,
                        hierarchyLevelsChosen:
                            hierarchyLevels[0] || initWizardExpensesDetailState.hierarchyLevelsChosen,
                    }),
                );
            }
            break;
        case EWizardExpensesDetailSteps.StepConfirmation:
            yield call(wizardReportStorageRestoreSaga, action);
            break;
    }
}

/*
 * -------------------------------------------
 *       SEND DATA ON NEXT STEP
 * -------------------------------------------
 */

export function* wizardExpensesDetailHandleNextSaga(): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());

    const date = yield select(makeSelectWizardExpensesDetailDate());
    const formatsAvailableChosen = yield select(makeSelectWizardExpensesDetailFormatsAvailableChosen());
    const hierarchyLevelsChosen: IWizardExpensesDetailHierarchyLevel = yield select(
        makeSelectWizardExpensesDetailHierarchyLevelsChosen(),
    );

    const params: { [key: string]: string | number | boolean } = {
        reportDate: date,
        format: formatsAvailableChosen,
        hierarchyLevel: hierarchyLevelsChosen.id,
    };
    switch (stepName) {
        case EWizardExpensesDetailSteps.StepSelectParams:
            yield put(
                wizardNextAction({
                    data: params,
                }),
            );
            break;
        case EWizardExpensesDetailSteps.StepConfirmation:
            const reportParams: IWizardReportOrderParams = yield select(makeSelectWizardReportOrderParams());
            const reportDestEmail: IWizardReportParam = yield select(
                makeSelectWizardReportParam(EWizardReportParam.EMAIL_DEST),
            );
            const reportNeedCompression: IWizardReportParam = yield select(
                makeSelectWizardReportParam(EWizardReportParam.ZIP),
            );
            const reportCompressionPassword: IWizardReportParam = yield select(
                makeSelectWizardReportParam(EWizardReportParam.ZIP_PASSWORD),
            );

            params.needCompression = reportNeedCompression.isEnabled || false;
            if (reportDestEmail.isEnabled) params.destEmail = reportDestEmail.value;
            if (reportCompressionPassword.isEnabled) params.compressionPassword = reportCompressionPassword.value;

            yield put(
                wizardNextAction({
                    data: params,
                    ...reportParams,
                }),
            );
            break;
    }

    yield call(wizardExpensesDetailUpdateStorageSaga);
}

export function* wizardExpensesDetailHandleNextSuccessSaga(action: TWizardNextSuccessAction): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());
    switch (stepName) {
        case EWizardExpensesDetailSteps.StepConfirmation:
            const orderId = action.payload.currentStep?.step?.order?.orderId ?? '';

            const isMegaPowersFeatureGreaterOrEqualThan2 = yield select(
                makeSelectIsFeatureWithVersionEnabled(FeatureNameEnum.MEGA_POWERS, 2, true),
            );

            if (isMegaPowersFeatureGreaterOrEqualThan2) {
                const { orderCreationModal }: ICommonTranslation = yield select(makeSelectTranslationCommon());

                const breadCrumbsData = yield select(makeSelectBreadCrumbsData());
                const goBack = () => breadCrumbGoBack(breadCrumbsData);

                yield put(
                    modalOpen({
                        render: () =>
                            createElement(ModalMultipleOrders, {
                                isOpen: true,
                                orderOrOrders: orderId,
                                onClose: goBack,
                                onSuccessButtonClick: breadCrumbGoBack,
                                orderTypeTranlation: orderCreationModal.success.orderType.CLIENT_EXPENSES_REPORT,
                            }),
                    }),
                );
            } else {
                yield put(wizardOpenModalSummaryAction({ orderId }));
            }
            break;
    }
}

/*
 * -------------------------------------------
 *       SAVE DATA TO STORAGE
 * -------------------------------------------
 */

export function* wizardExpensesDetailUpdateStorageSaga(): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());
    switch (stepName) {
        case EWizardExpensesDetailSteps.StepSelectParams:
            const date = yield select(makeSelectWizardExpensesDetailDate());
            const formatsAvailableChosen = yield select(makeSelectWizardExpensesDetailFormatsAvailableChosen());
            const hierarchyLevelsChosen = yield select(makeSelectWizardExpensesDetailHierarchyLevelsChosen());
            yield put(
                wizardStorageUpdateStepAction({
                    date,
                    formatsAvailableChosen,
                    hierarchyLevelsChosen,
                }),
            );
            break;
    }
}

export function* wizardExpensesDetailStorageSaveSaga(): SagaIterator {
    const params = {};
    yield put(wizardStorageSaveStepAction({ params }));
}

export function* wizardExpensesDetailLoadDataFromStorageSaga(): SagaIterator<TWizardExpensesDetailUpdateDataPayload> {
    const localStorageState = yield call(wizardStorageLoadStepsSaga);
    const basketSnapshotId = yield select(makeSelectWizardBasketSnapshotId());
    const localStorageParams: TWizardExpensesDetailUpdateDataPayload = localStorageState[basketSnapshotId];

    if (localStorageParams) {
        yield put(
            wizardExpensesDetailUpdateDataAction({
                date: localStorageParams.date || initWizardExpensesDetailState.date,
                formatsAvailableChosen:
                    localStorageParams.formatsAvailableChosen || initWizardExpensesDetailState.formatsAvailableChosen,
                hierarchyLevelsChosen:
                    localStorageParams.hierarchyLevelsChosen || initWizardExpensesDetailState.hierarchyLevelsChosen,
            }),
        );
    }

    return localStorageParams;
}

export function* wizardExpensesDetailValidateSaga(): SagaIterator {
    let isValid = true;
    let error = '';
    const wizardExpensesDetailTranslation: IWizardExpensesDetailTranslation = yield select(
        makeSelectTranslationWizardExpensesDetail(),
    );
    const stepName = yield select(makeSelectWizardStepName());
    switch (stepName) {
        case EWizardExpensesDetailSteps.StepSelectParams:
            const formatsAvailableChosen = yield select(makeSelectWizardExpensesDetailFormatsAvailableChosen());
            const hierarchyLevelsChosen = yield select(makeSelectWizardExpensesDetailHierarchyLevelsChosen());
            const date = yield select(makeSelectWizardExpensesDetailDate());
            isValid = !!formatsAvailableChosen && !!hierarchyLevelsChosen && !!date;
            if (!isValid) {
                error = wizardExpensesDetailTranslation.validateErrors.default;
            } else if (!date) {
                error = wizardExpensesDetailTranslation.validateErrors.selectDate;
            }
            break;
        case EWizardExpensesDetailSteps.StepConfirmation:
            isValid = yield call(wizardReportValidateSaga);
            break;
    }

    yield put(wizardExpensesDetailValidationAction({ error, isValid }));
}

/*
 * -------------------------------------------
 *              Failure Handler
 * -------------------------------------------
 */

export function* wizardExpensesDetailFailedSaga(action: any): SagaIterator {
    const errorCode = action.payload?.response?.error?.code;
    const httpStatus = action.payload?.code;
    let title: string;

    const translationCommon: ICommonTranslation = yield select(makeSelectTranslationCommon());

    if (httpStatus === HttpStatus.NOT_ACCEPTABLE) {
        yield call(wizardModalTimeoutSaga);
        return;
    } else if (httpStatus === HttpStatus.NOT_FOUND) {
        return;
    } else if (errorCode) {
        const translationExpensesDetails = yield select(makeSelectTranslationWizardExpensesDetail());
        const wizardTranslation = yield select(makeSelectWizardTranslation());
        title =
            translationExpensesDetails.error.codes[errorCode] ||
            translationCommon.error.codes[errorCode] ||
            wizardTranslation.wizardModalError.default;
    }

    yield put(
        modalOpen({
            render: (props: IModalRenderProps) =>
                createElement(ModalErrorCommon, {
                    onCloseModal: props.onCloseModal,
                    title,
                    translation: translationCommon.modal,
                }),
        }),
    );
}

/*
 * -------------------------------------------
 *       Wizard Sagas
 * -------------------------------------------
 */

export function* wizardExpensesDetailSagas(): SagaIterator {
    yield all([
        // initialization
        takeEvery(WIZARD_START_SUCCESS, wizardExpensesDetailStorageSaveSaga),
        takeEvery(WIZARD_META_SUCCESS, wizardExpensesDetailMetaSaga),
        // next
        takeEvery(WIZARD_EXPENSES_DETAIL_HANDLE_NEXT, wizardExpensesDetailHandleNextSaga),
        takeEvery([WIZARD_NEXT_SUCCESS, WIZARD_BACK_SUCCESS], wizardExpensesDetailLoadDataFromStorageSaga),
        takeEvery(WIZARD_NEXT_SUCCESS, wizardExpensesDetailHandleNextSuccessSaga),
        // validation
        takeEvery(
            [WIZARD_EXPENSES_DETAIL_CHECK_VALIDATION, WIZARD_EXPENSES_DETAIL_UPDATE_DATA],
            wizardExpensesDetailValidateSaga,
        ),
        // failed
        takeEvery(
            [WIZARD_BACK_FAILED, WIZARD_NEXT_FAILED, WIZARD_RESTORE_FAILED, WIZARD_START_FAILED],
            wizardExpensesDetailFailedSaga,
        ),
    ]);
}
